// ScheduleManager.js - Komponent for att hantera behandlarscheman

import React, { useState, useEffect, useContext } from 'react';
import { getUserSchedule, updateUserSchedule, getClinicUsers } from './calendarApi';
import { UserContext } from '../../UserContext';
import './Calendar.css';

const WEEKDAYS = [
  { id: 0, name: 'Mandag' },
  { id: 1, name: 'Tisdag' },
  { id: 2, name: 'Onsdag' },
  { id: 3, name: 'Torsdag' },
  { id: 4, name: 'Fredag' },
  { id: 5, name: 'Lordag' },
  { id: 6, name: 'Sondag' }
];

const DEFAULT_SCHEDULE = WEEKDAYS.reduce((acc, day) => {
  acc[day.id] = {
    active: day.id < 5, // Aktiv mån-fre
    start_time: '08:00',
    end_time: '17:00'
  };
  return acc;
}, {});

const ScheduleManager = ({ onClose }) => {
  const { user: currentUser } = useContext(UserContext);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [clinicUsers, setClinicUsers] = useState([]);
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [validFrom, setValidFrom] = useState(''); // Datum när schemat börjar gälla
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Ladda användare
  useEffect(() => {
    const loadUsers = async () => {
      try {
        const users = await getClinicUsers({ calendarOnly: true });
        const filteredUsers = users.filter(u => u.clinic_id === currentUser?.clinic_id);
        setClinicUsers(filteredUsers);

        // Välj första användaren som standard
        if (filteredUsers.length > 0 && !selectedUserId) {
          setSelectedUserId(String(filteredUsers[0].user_id));
        } else if (filteredUsers.length === 0) {
          // Utan vald användare körs loadSchedule aldrig - släck spinnern här
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading users:', err);
        setError('Kunde inte ladda användare');
        setLoading(false);
      }
    };

    if (currentUser) {
      loadUsers();
    }
  }, [currentUser, selectedUserId]);

  // Ladda schema för vald användare
  useEffect(() => {
    const loadSchedule = async () => {
      if (!selectedUserId) return;

      try {
        setLoading(true);
        setError(null);
        const data = await getUserSchedule(parseInt(selectedUserId));

        if (data.schedule) {
          // Konvertera från API-format till UI-format
          const formattedSchedule = { ...DEFAULT_SCHEDULE };
          Object.keys(data.schedule).forEach(weekday => {
            const daySchedule = data.schedule[weekday];
            formattedSchedule[weekday] = {
              active: daySchedule.active,
              start_time: daySchedule.start_time || '08:00',
              end_time: daySchedule.end_time || '17:00'
            };
          });
          setSchedule(formattedSchedule);
        } else {
          setSchedule(DEFAULT_SCHEDULE);
        }
      } catch (err) {
        // Om schemat inte finns, använd default
        if (err.response?.status === 404) {
          setSchedule(DEFAULT_SCHEDULE);
        } else {
          console.error('Error loading schedule:', err);
          setError('Kunde inte ladda schema');
        }
      } finally {
        setLoading(false);
      }
    };

    loadSchedule();
  }, [selectedUserId]);

  // Uppdatera en dag
  const handleDayChange = (weekday, field, value) => {
    setSchedule(prev => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        [field]: value
      }
    }));
    setSuccess(null);
  };

  // Aktivera/inaktivera dag
  const handleToggleDay = (weekday) => {
    setSchedule(prev => ({
      ...prev,
      [weekday]: {
        ...prev[weekday],
        active: !prev[weekday].active
      }
    }));
    setSuccess(null);
  };

  // Kopiera till alla vardagar
  const copyToWeekdays = (weekday) => {
    const sourceDay = schedule[weekday];
    setSchedule(prev => {
      const newSchedule = { ...prev };
      // Kopiera till måndag-fredag (0-4)
      for (let i = 0; i < 5; i++) {
        newSchedule[i] = {
          ...sourceDay
        };
      }
      return newSchedule;
    });
    setSuccess(null);
  };

  // Spara schema
  const handleSave = async () => {
    if (!selectedUserId) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await updateUserSchedule(parseInt(selectedUserId), schedule, validFrom || null);
      const successMsg = validFrom
        ? `Schema sparat! Galler fran ${validFrom}`
        : 'Schema sparat!';
      setSuccess(successMsg);
    } catch (err) {
      console.error('Error saving schedule:', err);
      // Behörighetsfel (clinic_admin_required) svarar med msg, övriga fel med error
      setError(err.response?.data?.error || err.response?.data?.msg || 'Kunde inte spara schema');
    } finally {
      setSaving(false);
    }
  };

  const selectedUser = clinicUsers.find(u => u.user_id === parseInt(selectedUserId));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content schedule-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Schemahantering</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          {error && <div className="calendar-error">{error}</div>}
          {success && <div className="calendar-success">{success}</div>}

          {/* Behandlarval */}
          <div className="form-group">
            <label>Behandlare</label>
            <select
              value={selectedUserId}
              onChange={e => setSelectedUserId(e.target.value)}
              className="user-select"
            >
              {clinicUsers.map(user => (
                <option key={user.user_id} value={user.user_id}>
                  {user.first_name} {user.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Gäller från datum */}
          <div className="form-group">
            <label>Galler fran (valfritt)</label>
            <input
              type="date"
              value={validFrom}
              onChange={e => setValidFrom(e.target.value)}
              className="valid-from-input"
            />
            <small className="form-help">
              Lamna tomt for att schemat ska galla direkt
            </small>
          </div>

          {loading ? (
            <div className="loading-text">Laddar schema...</div>
          ) : (
            <>
              <div className="schedule-info">
                <i className="fas fa-info-circle"></i>
                Ange arbetstider for {selectedUser?.first_name || 'behandlaren'}.
                Bokningar utanfor dessa tider visas med varning.
              </div>

              {/* Schema-tabell */}
              <div className="schedule-table">
                <div className="schedule-header">
                  <div className="col-day">Dag</div>
                  <div className="col-active">Arbetsdag</div>
                  <div className="col-time">Start</div>
                  <div className="col-time">Slut</div>
                  <div className="col-actions"></div>
                </div>

                {WEEKDAYS.map(day => (
                  <div
                    key={day.id}
                    className={`schedule-row ${!schedule[day.id].active ? 'inactive' : ''}`}
                  >
                    <div className="col-day">{day.name}</div>
                    <div className="col-active">
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={schedule[day.id].active}
                          onChange={() => handleToggleDay(day.id)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                    <div className="col-time">
                      <input
                        type="time"
                        value={schedule[day.id].start_time}
                        onChange={e => handleDayChange(day.id, 'start_time', e.target.value)}
                        disabled={!schedule[day.id].active}
                      />
                    </div>
                    <div className="col-time">
                      <input
                        type="time"
                        value={schedule[day.id].end_time}
                        onChange={e => handleDayChange(day.id, 'end_time', e.target.value)}
                        disabled={!schedule[day.id].active}
                      />
                    </div>
                    <div className="col-actions">
                      {day.id < 5 && (
                        <button
                          type="button"
                          className="btn-icon copy-btn"
                          onClick={() => copyToWeekdays(day.id)}
                          title="Kopiera till alla vardagar"
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <div className="right-buttons">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Stang
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving || loading}
            >
              {saving ? 'Sparar...' : 'Spara schema'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScheduleManager;
